import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  CheckCircleOutline as OkIcon,
  ContentCopy as CopyIcon,
  ErrorOutline as ErrIcon,
  PlayArrow as PlayIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { PageShell } from '@/layout/PageShell';
import { StatusPill } from '@/components/StatusPill';
import { useToast } from '@/components/Toaster';
import {
  explorer,
  type ExplorerCatalog,
  type ExplorerField,
  type ExplorerStep,
  type ExplorerRunResult,
  type IntegrationKit,
} from '@/api/explorer';
import { tokenStore } from '@/auth/tokenStore';
import { useAuth } from '@/auth/AuthContext';
import { M2ConsultationForm } from './integration-testing/M2ConsultationForm';
import { M3ConsentForm } from './integration-testing/M3ConsentForm';

/**
 * Tenant API Explorer (Integration Workbench).
 *
 * Three-pane layout:
 *   Left rail   — flows + steps, filtered by the tenant's plan tier
 *   Center      — workbench: Form | Raw | Headers tabs + Run button + response
 *   Right rail  — integration kit: cURL / JS / Python / Java / Postman snippets
 *
 * Successful calls capture pointer values (txnId, XToken, …) into a
 * session map; subsequent forms auto-fill from that map so the
 * end-to-end flows chain naturally without copy-paste.
 */
export function ApiExplorerPage() {
  const theme = useTheme();
  const toast = useToast();

  const { data: catalog, isLoading, error, mutate } = useSWR<ExplorerCatalog>(
    'explorer-catalog',
    () => explorer.catalog(),
  );

  // session-scoped state: captured pointers + active step + last result
  const [captured, setCaptured] = useState<Record<string, unknown>>({});
  const [activeApiCode, setActiveApiCode] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, ExplorerRunResult>>({});

  // pick the first step of the first flow on initial load
  useEffect(() => {
    if (!activeApiCode && catalog?.flows?.length) {
      const first = catalog.flows[0]?.steps?.[0];
      if (first) setActiveApiCode(first.apiCode);
    }
  }, [catalog, activeApiCode]);

  const activeStep: ExplorerStep | null = useMemo(() => {
    if (!catalog || !activeApiCode) return null;
    for (const f of catalog.flows) {
      const s = f.steps.find((st) => st.apiCode === activeApiCode);
      if (s) return s;
    }
    return null;
  }, [catalog, activeApiCode]);

  const onRunResult = (r: ExplorerRunResult) => {
    setResults((prev) => ({ ...prev, [r.apiCode]: r }));
    if (r.ok && r.captured && Object.keys(r.captured).length > 0) {
      setCaptured((prev) => ({ ...prev, ...r.captured }));
      toast.success(
        'Captured for next step',
        Object.keys(r.captured).map((k) => `${k}=${truncate(String(r.captured[k]), 16)}`).join(' · '),
      );
    }
  };

  return (
    <PageShell
      kicker="TENANT · API EXPLORER"
      title="Integration Workbench"
      subtitle="Test every endpoint. Successful calls capture working payloads + headers + multi-language snippets you paste straight into your HIMS."
      width="full"
      actions={
        <IconButton onClick={() => mutate()} aria-label="refresh"><RefreshIcon fontSize="small" /></IconButton>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn&apos;t load the catalog — {(error as Error).message}
        </Alert>
      )}
      {isLoading && <CircularProgress size={20} />}

      {catalog && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '260px 1fr 360px' }, gap: 2 }}>
          {/* Left rail */}
          <Box
            sx={{
              border: `1px solid ${theme.palette.border.subtle}`,
              borderRadius: theme.radius.md + 'px',
              bgcolor: theme.palette.surface.card,
              p: 1,
              maxHeight: 'calc(100vh - 240px)',
              overflowY: 'auto',
            }}
          >
            {catalog.flows.map((f) => (
              <Box key={f.flowCode} sx={{ mb: 2 }}>
                <Typography variant="overline" color="text.tertiary" sx={{ pl: 1, display: 'block' }}>
                  {f.flowName}
                </Typography>
                <Stack spacing={0.25}>
                  {f.steps.map((s) => {
                    const r = results[s.apiCode];
                    const isActive = activeApiCode === s.apiCode;
                    return (
                      <Box
                        key={s.apiCode}
                        onClick={() => setActiveApiCode(s.apiCode)}
                        sx={{
                          px: 1,
                          py: 0.75,
                          borderRadius: theme.radius.sm + 'px',
                          cursor: 'pointer',
                          bgcolor: isActive ? theme.palette.brand.lighter : 'transparent',
                          borderLeft: `3px solid ${
                            r?.ok ? theme.palette.status.success
                              : r && !r.ok ? theme.palette.status.danger
                              : isActive ? theme.palette.brand.main
                              : 'transparent'}`,
                          '&:hover': { bgcolor: isActive ? theme.palette.brand.lighter : theme.palette.surface.sunken },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="caption" sx={{ width: 18, color: 'text.tertiary' }}>{s.sequenceIndex}.</Typography>
                          <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" noWrap fontWeight={isActive ? 600 : 400} sx={{ fontSize: 13 }}>
                              {s.name}
                            </Typography>
                            <Typography variant="caption" color="text.tertiary" noWrap sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10 }}>
                              {s.httpMethod} {s.pathPattern.length > 28 ? s.pathPattern.slice(0, 28) + '…' : s.pathPattern}
                            </Typography>
                          </Stack>
                          {r?.ok && <OkIcon sx={{ fontSize: 14, color: theme.palette.status.success }} />}
                          {r && !r.ok && <ErrIcon sx={{ fontSize: 14, color: theme.palette.status.danger }} />}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Box>

          {/* Center workbench */}
          <Box>
            {activeStep ? (
              <Workbench
                step={activeStep}
                tenantDefaults={catalog.tenantDefaults}
                captured={captured}
                lastResult={results[activeStep.apiCode]}
                onResult={onRunResult}
              />
            ) : (
              <Alert severity="info">No endpoints visible for your plan. Contact your platform admin.</Alert>
            )}
          </Box>

          {/* Right rail */}
          <Box>
            {activeStep && <IntegrationKitPanel apiCode={activeStep.apiCode} success={!!results[activeStep.apiCode]?.ok} />}
          </Box>
        </Box>
      )}
    </PageShell>
  );
}

// =====================================================================
// Workbench (form + raw + headers + response)
// =====================================================================

function Workbench({
  step,
  tenantDefaults,
  captured,
  lastResult,
  onResult,
}: {
  step: ExplorerStep;
  tenantDefaults: Record<string, unknown>;
  captured: Record<string, unknown>;
  lastResult?: ExplorerRunResult;
  onResult: (r: ExplorerRunResult) => void;
}) {
  const theme = useTheme();
  const toast = useToast();
  const { claims } = useAuth();
  const guidedAvailable = step.apiCode === 'M2.CONSULTATION.PUSH' || step.apiCode === 'M3.CONSENT.REQUEST.V3';
  const [tab, setTab] = useState<'form' | 'raw' | 'headers' | 'guided'>(guidedAvailable ? 'guided' : 'form');
  const [running, setRunning] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fieldValues, setFieldValues] = useState<Record<string, unknown>>({});
  const [rawJson, setRawJson] = useState<string>(JSON.stringify(step.exampleRequest ?? {}, null, 2));

  // re-seed fieldValues whenever step / captured / tenantDefaults change.
  useEffect(() => {
    const seed: Record<string, unknown> = {};
    for (const f of step.fields) {
      // priority: prior captured > tenant default > example value
      if (captured[f.name] !== undefined) seed[f.name] = captured[f.name];
      else if (f.defaultFromTenant && tenantDefaults[f.defaultFromTenant] !== undefined) {
        seed[f.name] = tenantDefaults[f.defaultFromTenant];
      } else if (f.exampleValue !== undefined) seed[f.name] = f.exampleValue;
      else seed[f.name] = '';
    }
    setFieldValues(seed);
    setRawJson(JSON.stringify(step.exampleRequest ?? {}, null, 2));
  }, [step, captured, tenantDefaults]);

  const setVal = (name: string, v: unknown) => setFieldValues((p) => ({ ...p, [name]: v }));

  const buildBody = (): Record<string, unknown> => {
    if (tab === 'raw') {
      try { return JSON.parse(rawJson); } catch { return {}; }
    }
    // Form mode: only include non-empty values; coerce numbers/booleans.
    const out: Record<string, unknown> = {};
    for (const f of step.fields) {
      const v = fieldValues[f.name];
      if (v === '' || v === undefined || v === null) continue;
      if (f.type === 'number') out[f.name] = Number(v);
      else if (f.type === 'boolean') out[f.name] = Boolean(v);
      else if (f.type === 'json') {
        try { out[f.name] = typeof v === 'string' ? JSON.parse(v as string) : v; } catch { out[f.name] = v; }
      } else out[f.name] = v;
    }
    return out;
  };

  const run = async () => {
    setRunning(true);
    try {
      const body = buildBody();
      // Path params: replace any {placeholder} from the form values.
      const pathParams: Record<string, unknown> = {};
      const pathRegex = /\{([^}]+)\}/g;
      let m;
      while ((m = pathRegex.exec(step.pathPattern))) {
        const key = m[1];
        if (key && body[key] !== undefined) {
          pathParams[key] = body[key];
          delete body[key];
        }
      }
      // GET: convert remaining body to query params.
      const isGet = step.httpMethod.toUpperCase() === 'GET';
      const r = await explorer.run({
        apiCode: step.apiCode,
        body: isGet ? undefined : body,
        pathParams,
        queryParams: isGet ? body : undefined,
      });
      onResult(r);
      if (r.ok) toast.success(`${step.name} · ${r.httpStatus} · ${r.durationMs}ms`);
      else toast.error(`${step.name} · ${r.httpStatus}`, r.decodedError ?? 'See response panel.');
    } catch (e) {
      toast.error('Run failed', (e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  const visibleFields = step.fields.filter((f) => f.type !== 'hidden' && (showAdvanced || !f.advanced));

  return (
    <Box sx={{ border: `1px solid ${theme.palette.border.subtle}`, borderRadius: theme.radius.md + 'px', bgcolor: theme.palette.surface.card }}>
      {/* Header */}
      <Box sx={{ p: 2.5, borderBottom: `1px solid ${theme.palette.border.subtle}` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={step.httpMethod} sx={{ fontWeight: 600, bgcolor: methodColor(step.httpMethod, theme), color: '#fff' }} />
              <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>{step.pathPattern}</Typography>
              <Tooltip title="Copy path"><IconButton size="small" onClick={() => navigator.clipboard.writeText(step.pathPattern)}><CopyIcon fontSize="inherit" /></IconButton></Tooltip>
            </Stack>
            <Typography variant="h2" sx={{ fontSize: 18, fontWeight: 600 }}>{step.name}</Typography>
            {step.description && <Typography variant="body2" color="text.secondary">{step.description}</Typography>}
          </Stack>
          <Button
            variant="contained"
            startIcon={running ? <CircularProgress size={14} /> : <PlayIcon />}
            onClick={run}
            disabled={running}
            sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
          >
            {running ? 'Running…' : 'Run'}
          </Button>
        </Stack>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: `1px solid ${theme.palette.border.subtle}`, minHeight: 36, '& .MuiTab-root': { minHeight: 36, textTransform: 'none' } }}>
        {guidedAvailable && <Tab value="guided" label="Guided · Certification" />}
        <Tab value="form" label="Form" />
        <Tab value="raw" label="Raw JSON" />
        <Tab value="headers" label="Headers" />
      </Tabs>

      {/* Tab content */}
      <Box sx={{ p: 2.5 }}>
        {tab === 'guided' && step.apiCode === 'M2.CONSULTATION.PUSH' && (
          <M2ConsultationForm
            context={{
              tenantId: String(tenantDefaults.tenantId ?? claims?.tenantId ?? ''),
              memberId: String(captured.memberId ?? ''),
              abhaAddress: String(captured.abhaAddress ?? ''),
              abhaNumber: String(captured.abhaNumber ?? ''),
              patientName: String((captured.profile as { name?: string } | undefined)?.name ?? ''),
              patientGender: String((captured.profile as { gender?: string } | undefined)?.gender ?? 'unknown'),
              patientBirthDate: String((captured.profile as { birthDate?: string } | undefined)?.birthDate ?? ''),
              facilityName: String(tenantDefaults.organizationName ?? ''),
              facilityId: String(tenantDefaults.hfrId ?? tenantDefaults.tenantId ?? ''),
              practitionerName: String(claims?.name ?? claims?.username ?? ''),
              practitionerId: String(claims?.username ?? ''),
            }}
            submitting={running}
            onSubmit={async (body) => {
              setRunning(true);
              try {
                const r = await explorer.run({ apiCode: 'M2.CONSULTATION.PUSH', body: body as unknown as Record<string, unknown> });
                onResult(r);
                if (r.ok) toast.success(`Consultation pushed · ${r.httpStatus} · ${r.durationMs}ms`);
                else toast.error(`Push failed · ${r.httpStatus}`, r.decodedError ?? 'See response panel.');
              } catch (e) { toast.error('Run failed', (e as Error).message); }
              finally { setRunning(false); }
            }}
          />
        )}
        {tab === 'guided' && step.apiCode === 'M3.CONSENT.REQUEST.V3' && (
          <M3ConsentForm
            context={{
              tenantId: String(tenantDefaults.tenantId ?? claims?.tenantId ?? ''),
              abhaAddress: String(captured.abhaAddress ?? ''),
              hiuId: String(tenantDefaults.hfrId ?? tenantDefaults.tenantId ?? ''),
              requesterName: String(claims?.name ?? claims?.username ?? ''),
              requesterRegNo: String(claims?.username ?? ''),
            }}
            submitting={running}
            onSubmit={async (body) => {
              setRunning(true);
              try {
                const r = await explorer.run({ apiCode: 'M3.CONSENT.REQUEST.V3', body });
                onResult(r);
                if (r.ok) toast.success(`Consent requested · ${r.httpStatus} · ${r.durationMs}ms`);
                else toast.error(`Consent failed · ${r.httpStatus}`, r.decodedError ?? 'See response panel.');
              } catch (e) { toast.error('Run failed', (e as Error).message); }
              finally { setRunning(false); }
            }}
          />
        )}
        {tab === 'form' && (
          <Stack spacing={1.75}>
            {visibleFields.map((f) => (
              <FieldControl
                key={f.name}
                field={f}
                value={fieldValues[f.name]}
                onChange={(v) => setVal(f.name, v)}
                hint={step.fieldSourceHints?.[f.name]}
                wasCaptured={captured[f.name] !== undefined}
              />
            ))}
            {step.fields.some((f) => f.advanced) && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch size="small" checked={showAdvanced} onChange={(_, v) => setShowAdvanced(v)} />
                <Typography variant="caption" color="text.secondary">Show advanced fields</Typography>
              </Stack>
            )}
          </Stack>
        )}
        {tab === 'raw' && (
          <TextField
            multiline
            fullWidth
            value={rawJson}
            onChange={(e) => setRawJson(e.target.value)}
            minRows={10}
            InputProps={{ sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: 12 } }}
          />
        )}
        {tab === 'headers' && (
          <Stack spacing={1}>
            <HeaderRow name="Authorization" value={`Bearer ${(tokenStore.get() ?? '').slice(0, 24)}…`} masked />
            <HeaderRow name="X-Tenant-Id" value="<from your session>" />
            <HeaderRow name="Content-Type" value="application/json" />
            <Typography variant="caption" color="text.tertiary">
              The explorer auto-attaches your bearer + tenant id. In production, your HIMS sets these from its own credential store.
            </Typography>
          </Stack>
        )}
      </Box>

      {/* Response panel */}
      {lastResult && (
        <Box sx={{ borderTop: `1px solid ${theme.palette.border.subtle}`, p: 2.5 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            <StatusPill tone={lastResult.ok ? 'success' : 'danger'}>
              {lastResult.httpStatus} {lastResult.ok ? 'OK' : 'FAIL'}
            </StatusPill>
            <Typography variant="caption" color="text.secondary">{lastResult.durationMs} ms</Typography>
            <Typography variant="caption" color="text.tertiary">{new Date(lastResult.ranAt).toLocaleTimeString()}</Typography>
          </Stack>
          {!lastResult.ok && lastResult.decodedError && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 1.5, borderRadius: theme.radius.sm + 'px' }}>
              <strong>What this means:</strong> {lastResult.decodedError}
            </Alert>
          )}
          {lastResult.captured && Object.keys(lastResult.captured).length > 0 && (
            <Box sx={{ mb: 1.5, p: 1.5, bgcolor: theme.palette.status.successBg ?? theme.palette.surface.sunken, borderRadius: theme.radius.sm + 'px' }}>
              <Typography variant="overline" color="text.tertiary" sx={{ display: 'block', mb: 0.5 }}>Captured for next step</Typography>
              {Object.entries(lastResult.captured).map(([k, v]) => (
                <Typography key={k} variant="caption" sx={{ display: 'block', fontFamily: 'JetBrains Mono, monospace' }}>
                  <strong>{k}:</strong> {truncate(JSON.stringify(v), 80)}
                </Typography>
              ))}
            </Box>
          )}
          <Box
            component="pre"
            sx={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 11,
              bgcolor: theme.palette.surface.sunken,
              borderRadius: theme.radius.sm + 'px',
              p: 1.5,
              m: 0,
              maxHeight: 280,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(lastResult.response, null, 2)}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function FieldControl({
  field,
  value,
  onChange,
  hint,
  wasCaptured,
}: {
  field: ExplorerField;
  value: unknown;
  onChange: (v: unknown) => void;
  hint?: string;
  wasCaptured: boolean;
}) {
  const theme = useTheme();
  const helper = (
    <Stack spacing={0.25}>
      {field.helperText && <Typography variant="caption" color="text.secondary">{field.helperText}</Typography>}
      {hint && (
        <Typography variant="caption" sx={{ color: theme.palette.brand.main, fontStyle: 'italic' }}>
          ↳ {hint}
        </Typography>
      )}
    </Stack>
  );

  if (field.type === 'select') {
    return (
      <TextField
        select
        size="small"
        label={field.label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        required={field.required}
        helperText={helper}
        InputProps={{ endAdornment: wasCaptured ? <InputAdornment position="end"><Chip label="captured" size="small" color="success" sx={{ height: 18, fontSize: 10 }} /></InputAdornment> : undefined }}
      >
        {(field.options ?? []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
      </TextField>
    );
  }
  if (field.type === 'boolean') {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Switch checked={!!value} onChange={(_, v) => onChange(v)} />
        <Typography variant="body2">{field.label}</Typography>
        {field.helperText && <Typography variant="caption" color="text.tertiary">— {field.helperText}</Typography>}
      </Stack>
    );
  }
  if (field.type === 'json') {
    return (
      <TextField
        label={field.label}
        value={typeof value === 'string' ? value : JSON.stringify(value ?? {}, null, 2)}
        onChange={(e) => onChange(e.target.value)}
        required={field.required}
        multiline
        minRows={5}
        helperText={helper}
        InputProps={{ sx: { fontFamily: 'JetBrains Mono, monospace', fontSize: 12 } }}
      />
    );
  }
  return (
    <TextField
      size="small"
      label={field.label}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      required={field.required}
      type={field.type === 'number' ? 'number' : 'text'}
      helperText={helper}
      placeholder={field.placeholder}
      InputProps={{ endAdornment: wasCaptured ? <InputAdornment position="end"><Chip label="captured" size="small" color="success" sx={{ height: 18, fontSize: 10 }} /></InputAdornment> : undefined }}
    />
  );
}

function HeaderRow({ name, value, masked }: { name: string; value: string; masked?: boolean }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Typography variant="caption" sx={{ minWidth: 140, fontFamily: 'JetBrains Mono, monospace' }}>{name}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'JetBrains Mono, monospace', flex: 1 }}>
        {masked ? value : value}
      </Typography>
    </Stack>
  );
}

// =====================================================================
// Right-rail integration kit
// =====================================================================

function IntegrationKitPanel({ apiCode, success }: { apiCode: string; success: boolean }) {
  const theme = useTheme();
  const toast = useToast();
  const [tab, setTab] = useState<keyof IntegrationKit['snippets']>('curl');
  const { data: kit, isLoading } = useSWR<IntegrationKit>(['kit', apiCode], () => explorer.integrationKit(apiCode));

  const copy = (label: string, value: string) => {
    navigator.clipboard.writeText(value).then(
      () => toast.success(`${label} copied`),
      () => toast.error('Copy failed', 'Select the text manually.'),
    );
  };

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.border.subtle}`,
        borderRadius: theme.radius.md + 'px',
        bgcolor: theme.palette.surface.card,
        position: 'sticky',
        top: 24,
        maxHeight: 'calc(100vh - 240px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.border.subtle}` }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="overline" color="text.tertiary">INTEGRATION KIT</Typography>
          {success && <Chip size="small" label="VERIFIED" color="success" sx={{ height: 18, fontSize: 10 }} />}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {success
            ? 'Captured working call — paste into your HIMS.'
            : 'Run the call once to verify it, then paste these into your HIMS.'}
        </Typography>
      </Box>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        sx={{ borderBottom: `1px solid ${theme.palette.border.subtle}`, minHeight: 32, '& .MuiTab-root': { minHeight: 32, textTransform: 'none', fontSize: 12, py: 0 } }}
      >
        <Tab value="curl" label="cURL" />
        <Tab value="javascript" label="JS" />
        <Tab value="python" label="Python" />
        <Tab value="java" label="Java" />
        <Tab value="postman" label="Postman" />
      </Tabs>
      <Box sx={{ p: 2, flex: 1, overflow: 'auto', minHeight: 0 }}>
        {isLoading && <CircularProgress size={16} />}
        {kit && (
          <>
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
              <Button size="small" startIcon={<CopyIcon fontSize="inherit" />} onClick={() => copy(tab, kit.snippets[tab])}>
                Copy
              </Button>
            </Stack>
            <Box
              component="pre"
              sx={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 10.5,
                bgcolor: theme.palette.surface.sunken,
                borderRadius: theme.radius.sm + 'px',
                p: 1.5,
                m: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {kit.snippets[tab]}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

function methodColor(m: string, theme: { palette: { brand: { main: string }; status: { success: string; warning: string; danger: string; info?: string } } }) {
  switch (m.toUpperCase()) {
    case 'GET':    return theme.palette.brand.main;
    case 'POST':   return theme.palette.status.success;
    case 'PUT':    return theme.palette.status.warning;
    case 'PATCH':  return theme.palette.status.warning;
    case 'DELETE': return theme.palette.status.danger;
    default:       return theme.palette.brand.main;
  }
}

function truncate(s: string, n: number) {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}
